import { useTranslation } from 'react-i18next'

/**
 * Suspense fallback used while a lazy-loaded page chunk is resolving.
 * Designed to feel like the editorial page is "developing" — warm shimmer
 * on a champagne-tinted gradient, no jarring grey blocks.
 */
export function PageSkeleton() {
  const { t } = useTranslation('common')

  return (
    <main
      id="main-content"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('aria.loading')}
      className="container-editorial min-h-[60vh] py-24 md:py-32"
    >
      {/* eyebrow */}
      <div
        className="skeleton-bar"
        style={{ width: '8rem', height: '0.75rem' }}
      />

      {/* heading lines */}
      <div className="mt-8 space-y-4">
        <div
          className="skeleton-bar"
          style={{ width: '70%', height: 'clamp(2.25rem, 4vw, 4rem)' }}
        />
        <div
          className="skeleton-bar"
          style={{ width: '52%', height: 'clamp(2.25rem, 4vw, 4rem)' }}
        />
      </div>

      {/* body lines */}
      <div className="mt-12 space-y-3 max-w-2xl">
        <div className="skeleton-bar" style={{ width: '100%', height: '0.875rem' }} />
        <div className="skeleton-bar" style={{ width: '94%', height: '0.875rem' }} />
        <div className="skeleton-bar" style={{ width: '78%', height: '0.875rem' }} />
      </div>

      <div className="hairline mt-16" />

      {/* image placeholder */}
      <div
        className="skeleton-bar mt-12"
        style={{
          width: '100%',
          height: 'clamp(14rem, 36vw, 24rem)',
          borderRadius: 'var(--radius-lg)',
        }}
      />

      <span className="sr-only">{t('aria.loading')}</span>

      <style>{`
        .skeleton-bar {
          position: relative;
          overflow: hidden;
          background-color: var(--color-border);
          border-radius: var(--radius-sm);
          opacity: 0.65;
        }
        .skeleton-bar::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image: linear-gradient(
            90deg,
            transparent 0%,
            rgba(200, 181, 139, 0.28) 45%,
            rgba(255, 255, 255, 0.4) 50%,
            rgba(200, 181, 139, 0.28) 55%,
            transparent 100%
          );
          transform: translateX(-100%);
          animation: skeleton-shimmer 1800ms cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        @keyframes skeleton-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .skeleton-bar::after { animation: none; }
        }
      `}</style>
    </main>
  )
}

export default PageSkeleton
